import { translate, TKey } from '../i18n';
import { readStoredLang, Lang } from '../contexts/LanguageContext';

export type InlineErrorCode =
  | 'FILE_TOO_LARGE'
  | 'COMBINED_SIZE_EXCEEDED'
  | 'TOO_MANY_FILES'
  | 'UNSUPPORTED_TYPE'
  | 'INVALID_FILENAME'
  | 'PRESIGN_FAILED'
  | 'UPLOAD_FAILED'
  | 'S3_REJECTED'
  | 'EXTRACTION_FAILED'
  | 'AGENT_TIMEOUT'
  | 'AGENT_UNAVAILABLE'
  | 'AGENT_ACCESS_DENIED'
  | 'AGENT_DEACTIVATED'
  | 'SESSION_CONFLICT'
  | 'AGENT_CONFIG_ERROR'
  | 'INVALID_STAGED_KEY'
  | 'USAGE_LIMIT'
  | 'CREDITS_EXHAUSTED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export class InlineError extends Error {
  readonly code: InlineErrorCode;
  readonly file?: string;
  constructor(code: InlineErrorCode, message: string, file?: string) {
    super(message);
    this.name = 'InlineError';
    this.code = code;
    this.file = file;
  }
}

/**
 * Codes the server only emits *before* it starts generating a reply — an agent
 * it refused to invoke, a config it couldn't parse, an upload it couldn't
 * verify. There is no detached producer behind these, so the send path must
 * surface them rather than trying to reconnect to a stream that never existed.
 *
 * SESSION_CONFLICT is deliberately absent: it means a reply *is* already being
 * generated, so reconnecting is the correct response to it.
 */
export const TERMINAL_STREAM_ERROR_CODES: ReadonlySet<InlineErrorCode> = new Set<InlineErrorCode>([
  'AGENT_ACCESS_DENIED',
  'AGENT_DEACTIVATED',
  'AGENT_CONFIG_ERROR',
  'INVALID_STAGED_KEY',
  // Rejected before any session or reply exists (HTTP 429).
  'USAGE_LIMIT',
  'CREDITS_EXHAUSTED',
]);

/**
 * User-facing text for a coded error, in the UI language (read from storage
 * so non-React callers like api.ts and uploadStaging.ts get it too).
 */
export function userMessageForCode(code: InlineErrorCode, file?: string, lang: Lang = readStoredLang()): string {
  const key: TKey = `chat.err.${code}`;
  const vars = { file: file ?? translate(lang, 'chat.err.file') };
  const text = translate(lang, key, vars);
  // A code the server added before this client knew about it.
  return text === key ? translate(lang, 'chat.err.UNKNOWN') : text;
}
